import React from 'react';
import { Handle, Position, NodeProps } from 'reactflow';
import { Box, Typography, Paper, IconButton, Tooltip } from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';

interface NodeData {
  label: string;
  nodeType: string;
  parameters?: any;
  description?: string;
  workflowId?: string;
  webhookSecret?: string;
}

// Recognise every variant of IF / Switch we may see on the canvas:
// - native WerkGang   (core.if, core.switch)
// - n8n imports       (n8n-nodes-base.if, n8n-nodes-base.switch)
// - lower/upper case  the registry sometimes preserves
// Without this an imported IF was rendered with a single source handle
// and the user had nowhere to attach the False branch.
const IF_NODE_TYPES = new Set<string>([
  'core.if',
  'n8n-nodes-base.if',
  'n8n-nodes-base.If',
  'n8n-nodes-base.IF',
]);
const SWITCH_NODE_TYPES = new Set<string>([
  'core.switch',
  'n8n-nodes-base.switch',
  'n8n-nodes-base.Switch',
]);

// Keep this list in sync with `WEBHOOK_TRIGGER_TYPES` in NodeInspector.tsx —
// every type that should expose a runnable WerkGang webhook URL on the card.
const WEBHOOK_TRIGGER_TYPES = new Set<string>([
  'trigger.webhook',
  'n8n-nodes-base.webhook',
  'n8n-nodes-base.webhookV1',
  'n8n-nodes-base.webhookV2',
  'n8n-nodes-base.webhookv1',
  'n8n-nodes-base.webhookv2',
  'n8n-nodes-base.formTrigger',
  'n8n-nodes-base.chatTrigger',
  '@n8n/n8n-nodes-langchain.chatTrigger',
]);

const isRealUuid = (v: string): boolean => /^[0-9a-f]{8}-[0-9a-f]{4}-/i.test(v || '');

const buildWebhookUrl = (workflowId: string, nodeId: string, secret: string): string => {
  const envApiUrl = String(process.env.REACT_APP_API_URL || '').trim().replace(/\/+$/, '');
  const baseOrigin = (() => {
    if (envApiUrl) return envApiUrl.replace(/\/api$/i, '');
    if (typeof window === 'undefined' || !window.location) return '';
    const { origin, hostname } = window.location;
    if (hostname === 'localhost' || hostname === '127.0.0.1') return 'http://127.0.0.1:8000';
    return origin;
  })();
  return `${baseOrigin}/webhook/${workflowId}/${nodeId}/${secret}/`;
};

export const BaseNode: React.FC<NodeProps<NodeData>> = ({ id, data, selected }) => {
  const isIfNode = IF_NODE_TYPES.has(String(data.nodeType || ''));
  const isSwitchNode = SWITCH_NODE_TYPES.has(String(data.nodeType || ''));
  const switchCases: Array<{ value: string }> = data.parameters?.cases || [];

  // Compute the runnable webhook URL for any webhook trigger variant. We
  // intentionally show this directly on the canvas so the user can copy/paste
  // it into external systems without opening the inspector. Falls back to a
  // friendly hint until the workflow has been saved (and the backend has
  // generated a real UUID + webhook_secret for the node).
  const isWebhookTrigger = WEBHOOK_TRIGGER_TYPES.has(data.nodeType || '');
  const webhookWorkflowId = String(data.workflowId ?? '').trim();
  const webhookSecret = String(data.webhookSecret ?? '').trim();
  const webhookReady = isRealUuid(webhookWorkflowId) && isRealUuid(String(id || '')) && webhookSecret.length > 8;
  const webhookUrl = webhookReady ? buildWebhookUrl(webhookWorkflowId, String(id), webhookSecret) : '';

  const handleCopyWebhook = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!webhookUrl) return;
    if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(webhookUrl).catch(() => {});
    }
  };

  // Categorise an n8n / langchain type onto our trigger / core / action palette
  // so imported workflows visually match natively-built ones instead of all
  // looking like grey "unknown" nodes.
  const classifyN8nType = (t: string): 'trigger' | 'core' | 'action' | null => {
    if (!t) return null;
    if (t.startsWith('n8n-nodes-base.')) {
      const tail = t.slice('n8n-nodes-base.'.length).toLowerCase();
      if (tail.endsWith('trigger') || tail === 'cron' || tail === 'interval' || tail === 'webhook') {
        return 'trigger';
      }
      const coreOps = new Set([
        'if', 'switch', 'merge', 'set', 'code', 'function', 'functionitem',
        'splitinbatches', 'splitout', 'limit', 'aggregate', 'wait', 'noop',
        'stickynote', 'itemlists', 'sort', 'filter', 'datetime', 'compare',
        'datatable', 'evaluation', 'executeworkflow',
      ]);
      if (coreOps.has(tail)) return 'core';
      return 'action';
    }
    if (t.startsWith('@n8n/n8n-nodes-langchain.')) {
      const tail = t.slice('@n8n/n8n-nodes-langchain.'.length).toLowerCase();
      if (tail.endsWith('trigger')) return 'trigger';
      // Memory / vector / embeddings / chat models behave like core helpers in
      // an LLM agent graph — keep them visually grouped with core.
      if (
        tail.startsWith('memory') ||
        tail.startsWith('vectorstore') ||
        tail.startsWith('embeddings') ||
        tail.startsWith('lmchat') ||
        tail.startsWith('outputparser') ||
        tail.startsWith('toolthink')
      ) {
        return 'core';
      }
      return 'action';
    }
    return null;
  };

  const getNodeColor = () => {
    const t = data.nodeType || '';
    if (t.startsWith('trigger')) return '#ff6b6b';
    if (t.startsWith('action') || t.startsWith('integration') || t.startsWith('crm') ||
        t.startsWith('erp') || t.startsWith('messaging') || t.startsWith('storage') ||
        t.startsWith('marketing') || t.startsWith('productivity')) {
      return '#4ecdc4';
    }
    if (t.startsWith('core') || t.startsWith('tool')) return '#45b7d1';
    const family = classifyN8nType(t);
    if (family === 'trigger') return '#ff6b6b';
    if (family === 'action') return '#4ecdc4';
    if (family === 'core') return '#45b7d1';
    return '#95a5a6';
  };

  return (
    <Paper
      elevation={selected ? 8 : 2}
      sx={{
        minWidth: 180,
        maxWidth: 280,
        maxHeight: 220,
        border: `2px solid ${selected ? getNodeColor() : 'transparent'}`,
        borderRadius: 2,
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          bgcolor: getNodeColor(),
          color: 'white',
          px: 2,
          py: 1,
        }}
      >
        <Typography variant="subtitle2" fontWeight="bold" noWrap>
          {data.label}
        </Typography>
      </Box>
      {isWebhookTrigger && (
        <Box
          className="nodrag"
          onClick={(e) => e.stopPropagation()}
          sx={{
            px: 1.25,
            py: 0.75,
            bgcolor: 'grey.100',
            borderBottom: '1px solid',
            borderColor: 'grey.300',
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
          }}
        >
          <Typography
            variant="caption"
            sx={{
              flex: 1,
              fontFamily: 'monospace',
              fontSize: '0.65rem',
              color: webhookReady ? 'text.primary' : 'warning.main',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              direction: webhookReady ? 'rtl' : 'ltr',
              textAlign: 'left',
            }}
            title={webhookReady ? webhookUrl : 'Save the workflow to generate a webhook URL'}
          >
            {webhookReady ? webhookUrl : 'Save workflow to get a URL'}
          </Typography>
          {webhookReady && (
            <Tooltip title="Copy webhook URL">
              <IconButton
                size="small"
                onClick={handleCopyWebhook}
                sx={{ p: 0.25 }}
                aria-label="Copy webhook URL"
              >
                <ContentCopyIcon sx={{ fontSize: 14 }} />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      )}
      {data.description && (
        <Box
          className="nowheel nodrag"
          onWheelCapture={(e) => e.stopPropagation()}
          sx={{
            px: 2,
            py: 1,
            maxHeight: 120,
            overflowY: 'auto',
            overflowX: 'hidden',
            pointerEvents: 'auto',
          }}
        >
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: 'block', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
          >
            {data.description}
          </Typography>
        </Box>
      )}

      <Handle type="target" position={Position.Top} style={{ background: '#555' }} />

      {isIfNode ? (
        <>
          <Handle
            id="true"
            type="source"
            position={Position.Bottom}
            style={{ left: '30%', background: '#4caf50', width: 12, height: 12 }}
            title="True branch"
          />
          <Handle
            id="false"
            type="source"
            position={Position.Bottom}
            style={{ left: '70%', background: '#f44336', width: 12, height: 12 }}
            title="False branch"
          />
          <Box sx={{ display: 'flex', justifyContent: 'space-between', px: '20%', pb: 0.5 }}>
            <Typography variant="caption" sx={{ color: '#4caf50', fontSize: '0.6rem', fontWeight: 'bold' }}>
              True
            </Typography>
            <Typography variant="caption" sx={{ color: '#f44336', fontSize: '0.6rem', fontWeight: 'bold' }}>
              False
            </Typography>
          </Box>
        </>
      ) : isSwitchNode ? (
        <>
          {switchCases.map((c, idx) => {
            const total = switchCases.length + 1;
            const leftPct = ((idx + 1) / (total + 1)) * 100;
            const colors = ['#4caf50', '#2196f3', '#ff9800', '#9c27b0', '#00bcd4', '#e91e63'];
            const color = colors[idx % colors.length];
            return (
              <React.Fragment key={`case-${idx}`}>
                <Handle
                  id={`case-${idx}`}
                  type="source"
                  position={Position.Bottom}
                  style={{ left: `${leftPct}%`, background: color, width: 10, height: 10 }}
                  title={`Case ${idx}: ${c.value}`}
                />
              </React.Fragment>
            );
          })}
          <Handle
            id="default"
            type="source"
            position={Position.Bottom}
            style={{ left: `${((switchCases.length + 1) / (switchCases.length + 2)) * 100}%`, background: '#757575', width: 10, height: 10 }}
            title="Default"
          />
          <Box sx={{ display: 'flex', justifyContent: 'space-around', px: 1, pb: 0.5 }}>
            {switchCases.map((c, idx) => {
              const colors = ['#4caf50', '#2196f3', '#ff9800', '#9c27b0', '#00bcd4', '#e91e63'];
              return (
                <Typography key={`lbl-${idx}`} variant="caption" sx={{ color: colors[idx % colors.length], fontSize: '0.55rem', fontWeight: 'bold' }}>
                  {c.value || `${idx}`}
                </Typography>
              );
            })}
            <Typography variant="caption" sx={{ color: '#757575', fontSize: '0.55rem', fontWeight: 'bold' }}>
              Def
            </Typography>
          </Box>
        </>
      ) : (
        <Handle type="source" position={Position.Bottom} style={{ background: '#555' }} />
      )}
    </Paper>
  );
};

















